import { useEffect, useState } from "react";
import { useProfileStore } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import type { IPaymentProfileListResponse } from "@/api/patient-request";
import { formatDate } from "@/utils/format";
import { toast } from "sonner";
import { confirmToast } from "@/utils/confirmToast";
import { FauxCreditCard, type CardBrand } from "./FauxCreditCard";

function getCardType(acctNo: string): CardBrand {
  if (!acctNo || !/^\d/.test(acctNo)) return "visa";
  if (/^4/.test(acctNo)) return "visa";
  if (/^(34|37)/.test(acctNo)) return "amex";
  if (/^5[1-5]/.test(acctNo)) return "mastercard";
  if (/^6011/.test(acctNo)) return "discover";
  if (/^9792/.test(acctNo)) return "troy";
  return "visa";
}

/** Format expDate to MM/yy for card display. Handles full date strings or MMYY. */
function expDisplay(expDate: string): string {
  if (!expDate) return "";
  const s = String(expDate).trim();
  if (/^\d{4}$/.test(s)) return s.slice(0, 2) + "/" + s.slice(2);
  try {
    return formatDate(s, "MM/yy");
  } catch {
    return s;
  }
}

export function PaymentCardList() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [cardlist, setCardlist] = useState<IPaymentProfileListResponse[]>([]);

  const facilityID = currentPatient?.facilityID?.toString() ?? "";
  const patientID = currentPatient?.patientID?.toString() ?? "";

  const loadList = () => {
    if (!facilityID || !patientID) return;
    patientRequest.request
      .getPaymentProfileList({ facilityID, id: patientID })
      .then((res) => setCardlist(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCardlist([]));
  };

  useEffect(() => {
    loadList();
  }, [facilityID, patientID]);

  const removeCC = async (item: IPaymentProfileListResponse) => {
    const confirmed = await confirmToast(
      "Are you sure you want to remove this card? You won't be able to revert this!",
      { confirmLabel: "Remove", cancelLabel: "Cancel", variant: "destructive" }
    );
    if (!confirmed) return;
    try {
      const res = await patientRequest.request.deletepaymentprofile({
        facilityId: facilityID,
        id: String(item.id),
        user: "Patient Portal",
      });
      if (res.data) {
        loadList();
        toast.success("Credit Card details has been successfully removed.");
      } else {
        toast.error("Failed to remove credit card details");
      }
    } catch (err) {
      toast.error("Failed to remove credit card details");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {cardlist.map((item) => (
        <div key={item.id} className="flex flex-col gap-3">
          <FauxCreditCard
            last4={item.accountNo}
            expDisplay={expDisplay(item.expDate)}
            brand={getCardType(item.accountNo)}
            nameOnAccount={item.nameOnAccount}
          />
          <button
            type="button"
            title="Remove Card"
            onClick={() => removeCC(item)}
            className="w-fit rounded-md border border-healthsource-lightLavender px-3 py-2 text-[12px] font-semibold text-healthsource-dark transition-colors hover:bg-red-50 hover:border-red-200 hover:text-red-700"
          >
            <i className="fas fa-trash mr-1.5" />
            Remove card
          </button>
        </div>
      ))}
    </div>
  );
}
